import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AgentQueue } from '../../../leads/agent/agent-queue.component';

/**
 * Leads Overview (design 3a): the manager's inbox. Three decisions come
 * here — let a prospect through, let a below-floor quote out, or say whether
 * two records are one site — and each card carries the evidence for its own
 * decision, because thirty seconds does not include opening another page.
 *
 * It is a queue rather than an edit session: work it to empty, with no staged
 * Save/Cancel flow, and say so when it is empty.
 *
 * It replaces the Leads workspace Command Center as the group's landing page. The
 * dashboards that page carries are a report, not an inbox; they stay where
 * they are, one rung down the nav.
 */
@Component({
  selector: 'app-leads-overview',
  templateUrl: './leads-overview.component.html',
  styleUrls: ['./leads-overview.component.css'],
})
export class LeadsOverviewComponent {
  /** Mirrors the queue's own load, so the headline counts the same fetch. */
  queue: AgentQueue | null = null;

  /** The three decisions the design names, in the order it names them. */
  readonly headlineKinds: readonly { kinds: readonly string[]; label: string; css: string }[] = [
    { kinds: ['StartPursuit'], label: 'to let through', css: 'po-go' },
    { kinds: ['QuoteException', 'GuardrailWarning'], label: 'on price', css: 'po-ex' },
    { kinds: ['IdentityReview'], label: 'same site?', css: 'po-rev' },
  ];

  constructor(private router: Router) {}

  onQueue(queue: AgentQueue): void {
    this.queue = queue;
  }

  countOf(kinds: readonly string[]): number {
    return (this.queue?.items ?? []).filter(i => kinds.includes(i.kind)).length;
  }

  get total(): number {
    return this.queue?.total ?? 0;
  }

  /** How long the oldest card has been waiting — the number a manager who has
   *  been away for a week actually wants first. */
  get oldestDays(): number | null {
    const stamps = (this.queue?.items ?? [])
      .map(i => i.sinceUtc)
      .filter((s): s is string => !!s)
      .map(s => new Date(s).getTime());
    if (!stamps.length) return null;
    const days = Math.floor((Date.now() - Math.min(...stamps)) / 86400000);
    return days > 0 ? days : null;
  }

  openBoard(): void {
    this.router.navigate(['/leads']);
  }

  openAnalytics(): void {
    this.router.navigate(['/leads/analysis']);
  }
}
